import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    Checkbox,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Collapse,
    IconButton,
    Divider,
    Grid,
    Button
} from '@material-ui/core';
import { Search, ExpandMore, ExpandLess, Folder, InsertDriveFile } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { addSelectedConcerning, removeSelectedConcerning } from '../../slices/moveSlice';
import { concernings } from '../../utils/concernings';
import { customerGetAll } from '../../api/review-1/review-1-customer';
import { companyGetAll } from '../../api/review-1/review-1-company';

interface MoveConcerningsPanelProps {
    height: number;
    onHide?: () => void;
}

interface TreeNode {
    id: string;
    label: string;
    children?: TreeNode[];
    isSelected?: boolean;
}

const MoveConcerningsPanel: React.FC<MoveConcerningsPanelProps> = ({ height, onHide }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const selectedConcernings = useSelector((state: RootState) => state.move.selectedConcernings);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
    const [clients, setClients] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Load clients and companies data
    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                const [clientsData, companiesData] = await Promise.all([
                    customerGetAll(),
                    companyGetAll()
                ]);

                if (clientsData !== 'error') {
                    setClients(clientsData || []);
                }

                if (companiesData && !companiesData.err) {
                    setCompanies(companiesData.items || []);
                }
            } catch (error) {
                console.error('Error loading clients and companies:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, []);

    // Convert concernings object to tree structure
    const buildConcerningsTree = (): TreeNode[] => {
        const tree: TreeNode[] = [];

        // Define the hierarchical structure
        const hierarchy = {
            'IpOp Review 1: Is there hope?': {
                items: ['solution'],
                children: {
                    'Clients': {
                        items: clients.map(client => `clients_${client.id}`),
                        dynamic: true,
                        data: clients
                    },
                    'Company and Investors': {
                        items: companies.map(company => `companies_${company.id}`),
                        dynamic: true,
                        data: companies
                    }
                }
            },
            'IpOp Review 2: Is it worth it?': {
                items: ['definition_of_success', 'definition_of_failure', 'activity_chain', 'economic_model', 'skills', 'all_benefits']
            },
            'IpOp Review 3: Influencers': {
                items: ['factors', 'stakeholders', 'collateral_damages']
            },
            'IpOp Review 4: Actions and Investment': {
                items: ['moves', 'intellectual_property', 'investment']
            }
        };

        // Build tree structure
        Object.keys(hierarchy).forEach(category => {
            const categoryData = hierarchy[category];
            const categoryNode: TreeNode = {
                id: `category-${category}`,
                label: t(category),
                children: []
            };

            // Add main category items
            categoryData.items.forEach(key => {
                if (concernings[key]) {
                    categoryNode.children!.push({
                        id: key,
                        label: t(concernings[key].label),
                        isSelected: selectedConcernings.includes(key)
                    });
                }
            });

            // Add sub-categories if they exist
            if (categoryData.children) {
                Object.keys(categoryData.children).forEach(subCategory => {
                    const subCategoryData = categoryData.children[subCategory];
                    const subCategoryNode: TreeNode = {
                        id: `subcategory-${category}-${subCategory}`,
                        label: t(subCategory),
                        children: []
                    };

                    if (subCategoryData.dynamic && subCategoryData.data) {
                        // Dynamic data (clients/companies) - use the items array from hierarchy
                        subCategoryNode.children = subCategoryData.items.map(itemId => {
                            // itemId is built as `<prefix>_<id>` (e.g. `companies_<uuid>`), where the
                            // prefix comes from the hierarchy items mapping, not the display label. Match
                            // on the id extracted from itemId rather than rebuilding a prefix from
                            // subCategory (which never matched "Company and Investors", showing the raw id).
                            const rawId = itemId.substring(itemId.indexOf('_') + 1);
                            const item = subCategoryData.data.find(d => `${d.id}` === rawId);
                            return {
                                id: itemId,
                                label: item ? (item.name || item.description || `Unknown ${subCategory.slice(0, -1)}`) : itemId,
                                isSelected: selectedConcernings.includes(itemId)
                            };
                        });
                    } else {
                        // Static concernings
                        subCategoryNode.children = subCategoryData.items.map(key => ({
                            id: key,
                            label: t(concernings[key]?.label || key),
                            isSelected: selectedConcernings.includes(key)
                        }));
                    }

                    categoryNode.children!.push(subCategoryNode);
                });
            }

            tree.push(categoryNode);
        });

        return tree;
    };

    const concerningsTree = buildConcerningsTree();

    // Filter tree based on search term
    const filterTree = (nodes: TreeNode[], search: string): TreeNode[] => {
        if (!search) return nodes;

        return nodes
            .map(node => {
                const filteredChildren = node.children
                    ? filterTree(node.children, search)
                    : undefined;

                const matchesSearch = node.label.toLowerCase().includes(search.toLowerCase()) ||
                    (filteredChildren && filteredChildren.length > 0);

                if (matchesSearch) {
                    return {
                        ...node,
                        children: filteredChildren
                    };
                }
                return null;
            })
            .filter(Boolean) as TreeNode[];
    };

    const filteredTree = filterTree(concerningsTree, searchTerm);

    const handleConcerningToggle = (concerningId: string) => {
        if (selectedConcernings.includes(concerningId)) {
            dispatch(removeSelectedConcerning(concerningId));
        } else {
            dispatch(addSelectedConcerning(concerningId));
        }
    };

    const handleNodeToggle = (nodeId: string) => {
        const newExpanded = new Set(expandedNodes);
        if (newExpanded.has(nodeId)) {
            newExpanded.delete(nodeId);
        } else {
            newExpanded.add(nodeId);
        }
        setExpandedNodes(newExpanded);
    };

    const renderTreeNode = (node: TreeNode, level: number = 0, forceExpanded: boolean = false) => {
        const isExpanded = forceExpanded || expandedNodes.has(node.id);
        const hasChildren = node.children && node.children.length > 0;
        const isLeaf = !hasChildren;

        return (
            <Box key={node.id}>
                <ListItem
                    sx={{
                        pl: level * 2 + 1,
                        py: 0.5,
                        cursor: isLeaf ? 'pointer' : 'default',
                        '&:hover': {
                            backgroundColor: isLeaf ? 'rgba(39, 171, 109, 0.04)' : 'transparent'
                        }
                    }}
                    onClick={() => {
                        if (isLeaf) {
                            handleConcerningToggle(node.id);
                        } else {
                            handleNodeToggle(node.id);
                        }
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                        {isLeaf ? (
                            <Checkbox
                                checked={selectedConcernings.includes(node.id)}
                                size="small"
                                sx={{
                                    p: 0,
                                    color: selectedConcernings.includes(node.id) ? 'rgb(39, 171, 109)' : 'text.secondary',
                                    '&.Mui-checked': { color: 'rgb(39, 171, 109)' },
                                }}
                            />
                        ) : (
                            <Folder sx={{ fontSize: 16, color: 'rgb(39, 171, 109)' }} />
                        )}
                    </ListItemIcon>

                    <ListItemText
                        primary={
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: isLeaf ? 400 : 500,
                                    color: selectedConcernings.includes(node.id) ? 'rgb(39, 171, 109)' : 'text.primary',
                                    fontSize: isLeaf ? '0.875rem' : '0.9rem'
                                }}
                            >
                                {node.label}
                            </Typography>
                        }
                    />

                    {hasChildren && !forceExpanded && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleNodeToggle(node.id);
                            }}
                        >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    )}
                </ListItem>

                {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List dense disablePadding>
                            {node.children!.map(child => renderTreeNode(child, level + 1, isExpanded && level === 0))}
                        </List>
                    </Collapse>
                )}
            </Box>
        );
    };

    if (isLoading) {
        return (
            <Grid item xs={4} sx={{
                backgroundColor: "rgba(39, 171, 109, 0.25)",
                overflowY: "scroll"
            }} padding={0} spacing={2} height={height}>
                <Grid container spacing={2} padding={4} sx={{ paddingLeft: 2 }}>
                    <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                        <Typography variant="h5" style={{ color: "rgb(39, 171, 109)" }}>
                            {t('Loading concernings...')}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid item xs={4} sx={{
            backgroundColor: "rgba(39, 171, 109, 0.25)",
            overflowY: "scroll"
        }} padding={0} spacing={2} height={height}>
            <Grid container spacing={2} padding={4} sx={{ paddingLeft: 2 }}>
                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h5" style={{ color: "rgb(39, 171, 109)" }}>
                            {t('Select Concernings')}
                            <Chip
                                color='default'
                                label={selectedConcernings.length}
                                size='small'
                                variant='filled'
                                sx={{ ml: 1 }}
                            />
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => onHide?.()}
                            sx={{ minWidth: 'auto', px: 2, py: 0.5, fontSize: '0.75rem' }}
                        >
                            {t('Hide')}
                        </Button>
                    </Box>
                </Grid>

                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={t('Search concernings...')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'background.paper',
                                '&:hover': {
                                    backgroundColor: 'background.paper',
                                },
                            },
                        }}
                    />
                </Grid>

                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                    <Box sx={{
                        maxHeight: height - 200,
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'rgba(39, 171, 109, 0.1)',
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(39, 171, 109, 0.3)',
                            borderRadius: '3px',
                            '&:hover': {
                                backgroundColor: 'rgba(39, 171, 109, 0.5)',
                            },
                        },
                    }}>
                        <List dense>
                            <ListItem
                                sx={{
                                    pl: 1,
                                    py: 0.5,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: 'rgba(39, 171, 109, 0.04)'
                                    }
                                }}
                                onClick={() => handleConcerningToggle('project')}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <Checkbox
                                        checked={selectedConcernings.includes('project')}
                                        size="small"
                                        sx={{
                                            p: 0,
                                            color: selectedConcernings.includes('project') ? 'rgb(39, 171, 109)' : 'text.secondary',
                                            '&.Mui-checked': { color: 'rgb(39, 171, 109)' },
                                        }}
                                    />
                                </ListItemIcon>

                                <ListItemText
                                    primary={
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 400,
                                                color: selectedConcernings.includes('project') ? 'rgb(39, 171, 109)' : 'text.primary',
                                                fontSize: '0.875rem'
                                            }}
                                        >
                                            {t('whole_project')}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                            {filteredTree.length > 0 ? (
                                filteredTree.map(node => renderTreeNode(node))
                            ) : (
                                <ListItem>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                {t('No concernings found')}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default MoveConcerningsPanel; 